import styled, { keyframes } from "styled-components";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { router } from "@inertiajs/react";
import {
  X,
  ExternalLink,
  LogIn,
  LogOut,
  Wallet,
  Banknote,
  RotateCw,
  RefreshCw,
  QrCode,
  MessageCircle,
  Mail,
  Pencil,
  AlertTriangle,
  ArrowLeftRight,
  Zap,
  Check,
  BedDouble,
  Clock,
  CheckCircle2,
  RotateCcw,
  Receipt,
  CalendarRange,
  Ban,
  FileSearch,
} from "lucide-react";

import { theme } from "./theme";
import StatusBadge from "./StatusBadge";
import RoomMoveModal from "./RoomMoveModal";
import PhoneField, { WA_COUNTRIES, detectWaCountry } from "./PhoneField";
import { physicalStatusMeta } from "./status";
import { apiFetch } from "./api";
import { formatDate, formatDateTime, timeWithDayPart } from "./dateFormat";
import { FoNotesButton, GuestNotesButton } from "./NotesButtons";
import VoidRefundDialog from "./VoidRefundDialog";
import PayoutFetchDialog from "./PayoutFetchDialog";
import BookingSourceIcon from "./BookingSourceIcon";
import RefundSendDialog from "./RefundSendDialog";
import PaymentDialog from "./payment/PaymentDialog";
import PayBadge from "./PayBadge";
import RescheduleModal from "./RescheduleModal";
import CancelBookingDialog from "./CancelBookingDialog";
import { readPaymentId, usePaymentUrlSync } from "./useDrawerUrl";
import { cleanComment } from "./text";
import { useT } from "./i18n";

// The FO's single workspace for one booking: summary + guest + rooms + payment + deposit + refund,
// headlined by ONE state-derived primary action ("what should I do next?"). Loads asynchronously with
// a skeleton; money actions arrive in the next increment (until then: "Buka di /admin").
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1400;
  background: rgba(26, 26, 26, 0.45);
  backdrop-filter: blur(2px);
`;

const slideIn = keyframes`
  from { transform: translateX(40px); opacity: 0.4; }
  to   { transform: translateX(0); opacity: 1; }
`;

const Panel = styled.aside`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 1401;
  width: min(680px, 100vw);
  display: flex;
  flex-direction: column;
  background: ${theme.canvas};
  border-left: 3px solid ${theme.gold};
  box-shadow: -18px 0 50px -20px rgba(26, 26, 26, 0.55);
  animation: ${slideIn} 0.22s ease;
`;

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 1rem 1.25rem 0.75rem;
  background: #ffffff;
  border-bottom: 1px solid ${theme.border};
`;

const HeadGuest = styled.div`
  align-items: center;
  display: inline-flex;
  gap: 0.4rem;
  font-size: 1.15rem;
  font-weight: 800;
  color: ${theme.ink};
`;

// A strict two-column label/value grid so every field sits in a fixed spot the eye can memorise.
const HeadMeta = styled.div`
  margin-top: 0.4rem;
  display: grid;
  grid-template-columns: auto 1fr;
  row-gap: 0.35rem;
  column-gap: 0.7rem;
  align-items: center;
  font-size: 1rem;
`;

const MetaLabel = styled.span`
  font-size: 1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: ${theme.muted};
`;

const MetaValue = styled.span`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  min-width: 0;
  font-size: 1rem;
  font-weight: 600;
  color: ${theme.ink700};
  word-break: break-word;
`;

const HeadActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

const AdminLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: ${theme.goldDark};
  text-decoration: none;
  white-space: nowrap;
  &:hover {
    text-decoration: underline;
  }
`;

const CloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: 1px solid ${theme.border};
  border-radius: 8px;
  background: #ffffff;
  cursor: pointer;
  &:hover {
    background: ${theme.goldSoft};
  }
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

// Pulsing gold glow for the "don't forget me" deposit buttons while the deposit is still unpaid.
const glow = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(201, 168, 108, 0.65), 0 0 6px 0 rgba(201, 168, 108, 0.5); }
  70% { box-shadow: 0 0 0 8px rgba(201, 168, 108, 0), 0 0 14px 3px rgba(201, 168, 108, 0.55); }
  100% { box-shadow: 0 0 0 0 rgba(201, 168, 108, 0), 0 0 6px 0 rgba(201, 168, 108, 0.5); }
`;

const RefreshButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: 1px solid ${theme.border};
  border-radius: 8px;
  background: #ffffff;
  color: ${theme.goldDark};
  cursor: pointer;
  &:hover {
    background: ${theme.goldSoft};
  }
  &[data-spin="true"] svg {
    animation: ${spin} 0.8s linear infinite;
  }
`;

const ChipButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.32rem 0.62rem;
  border: 1.5px solid ${theme.goldDark};
  border-radius: 8px;
  background: ${theme.goldSoft};
  color: ${theme.goldDark};
  font-size: 0.82rem;
  font-weight: 800;
  line-height: 1;
  cursor: pointer;
  vertical-align: middle;
  transition:
    background 0.12s,
    color 0.12s,
    border-color 0.12s;
  &:hover {
    background: ${theme.goldDark};
    color: #ffffff;
    border-color: ${theme.goldDark};
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

// Hover tooltip: wrap a control in <TipWrap> and add <Tip className="fdtip">…</Tip> as a sibling of
// the control so it reveals on hover. Used to explain the compact QR / WA / edit chips.
const TipWrap = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  &:hover .fdtip,
  &:focus-within .fdtip {
    opacity: 1;
    visibility: visible;
    transform: translate(-50%, 0);
  }
`;

const Tip = styled.span`
  position: absolute;
  top: calc(100% + 7px);
  left: 50%;
  transform: translate(-50%, -4px);
  width: max-content;
  max-width: 230px;
  padding: 0.4rem 0.6rem;
  border-radius: 7px;
  background: ${theme.ink};
  color: #ffffff;
  font-size: 0.74rem;
  font-weight: 600;
  line-height: 1.35;
  text-align: center;
  white-space: normal;
  opacity: 0;
  visibility: hidden;
  transition:
    opacity 0.12s,
    transform 0.12s;
  z-index: 1600;
  pointer-events: none;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.2);
  &::after {
    content: "";
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-bottom-color: ${theme.ink};
  }
`;

const QrBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1500;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(26, 26, 26, 0.72);
  padding: 1.5rem;
`;

const QrCard = styled.div`
  background: #ffffff;
  border-radius: 14px;
  padding: 1.5rem;
  text-align: center;
  max-width: 90vw;
`;

const QrLargeImg = styled.img`
  width: 360px;
  max-width: 78vw;
  height: auto;
  display: block;
  margin: 0 auto;
`;

const QrCodeText = styled.div`
  margin-top: 0.9rem;
  font-family: monospace;
  font-size: 1.35rem;
  font-weight: 700;
  color: ${theme.ink};
  letter-spacing: 0.06em;
`;

const QrHint = styled.div`
  margin-top: 0.4rem;
  font-size: 0.78rem;
  color: ${theme.muted};
`;

const QrCloseButton = styled.button`
  margin-top: 1.1rem;
  padding: 0.4rem 1.4rem;
  border: 1px solid ${theme.border};
  border-radius: 8px;
  background: ${theme.canvas};
  color: ${theme.ink};
  font-weight: 600;
  cursor: pointer;
`;

const StatusChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.1rem 0.5rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  color: ${(props) => props.$fg};
  background: ${(props) => props.$bg};
`;

const Body = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 1rem 1.25rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
`;

const PrimaryCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  background: ${(props) => props.$bg || "#ffffff"};
  border: 1px solid ${(props) => props.$border || theme.border};
`;

const PrimaryText = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: ${theme.ink};
`;

const PrimaryHint = styled.div`
  margin-top: 0.15rem;
  font-size: 1rem;
  font-weight: 400;
  color: ${theme.ink700};
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.55rem 1rem;
  border: 0;
  border-radius: 9px;
  background: ${theme.ink};
  color: #ffffff;
  font-size: 0.88rem;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    background: ${theme.inkSoft};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// "Deposit paid, guest not checked in yet" — the QRH next-step nudge for the FO.
const NextStepCard = styled.div`
  padding: 0.9rem 1rem;
  border-radius: 12px;
  background: #eaf7ef;
  border: 1px solid #b7e0c6;
`;

const NextStepTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 1rem;
  font-weight: 800;
  color: #166534;
`;

const NextStepIntro = styled.div`
  margin-top: 0.2rem;
  font-size: 0.95rem;
  color: ${theme.ink700};
`;

const NextStepList = styled.ol`
  margin: 0.65rem 0 0;
  padding: 0;
  list-style: none;
  counter-reset: ns;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
`;

const NextStepItem = styled.li`
  counter-increment: ns;
  position: relative;
  padding-left: 1.95rem;
  font-size: 0.95rem;
  line-height: 1.4;
  color: ${theme.ink};
  &::before {
    content: counter(ns);
    position: absolute;
    left: 0;
    top: 0;
    width: 1.45rem;
    height: 1.45rem;
    border-radius: 50%;
    background: #166534;
    color: #ffffff;
    font-size: 0.8rem;
    font-weight: 800;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const NextStepNote = styled.div`
  margin-top: 0.65rem;
  padding: 0.5rem 0.7rem;
  border-radius: 8px;
  background: ${theme.goldSoft};
  border-left: 4px solid ${theme.goldDark};
  font-size: 0.9rem;
  font-weight: 700;
  color: ${theme.ink};
`;

const Section = styled.section`
  background: #ffffff;
  border: 1px solid ${theme.border};
  border-radius: 12px;
  padding: 0.85rem 1rem;
`;

const SectionTitle = styled.h3`
  margin: 0 0 0.6rem;
  font-size: 1rem;
  font-weight: 700;
  color: ${theme.muted};
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const KV = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.25rem 0;
  font-size: 1rem;
  color: ${theme.ink700};
  strong {
    color: ${theme.ink};
    text-align: right;
  }
`;

const LineCard = styled.div`
  border: 1px solid ${theme.border};
  border-left: 3px solid ${(props) => props.$accent || theme.border};
  border-radius: 10px;
  padding: 0.6rem 0.7rem;
  margin-bottom: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  background: ${(props) => (props.$anchor ? theme.goldSoft : "#ffffff")};
`;

const LineHead = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const LineRoom = styled.span`
  font-size: 1.1rem;
  font-weight: 800;
  color: ${theme.ink};
`;

const LineType = styled.span`
  font-size: 1rem;
  color: ${theme.muted};
  flex: 1 1 auto;
`;

const LineDates = styled.div`
  font-size: 1rem;
  color: ${theme.ink700};
`;

const LineDeposit = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.95rem;
  color: ${theme.ink700};
`;

const SmallButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: ${(props) => (props.$emph ? "0.5rem 1rem" : "0.4rem 0.8rem")};
  border: 0;
  border-radius: 8px;
  background: ${(props) => (props.$emph ? theme.goldDark : theme.ink)};
  color: #ffffff;
  font-size: 1rem;
  font-weight: ${(props) => (props.$emph ? 800 : 700)};
  cursor: pointer;
  animation: ${(props) => (props.$emph ? glow : "none")} 1.6s ease-in-out
    infinite;
  &:hover {
    background: ${(props) => (props.$emph ? "#74592f" : theme.inkSoft)};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    animation: none;
  }
`;

const Blockers = styled.div`
  font-size: 1rem;
  color: #991b1b;
  line-height: 1.4;
`;

const ActionBar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.6rem;
  padding-top: 0.6rem;
  border-top: 1px dashed ${theme.border};
  font-size: 0.78rem;
  color: ${theme.ink700};
`;

// Per-line add-ons (extra bed + early check-in): a dashed sub-block inside the room card.
// Read-only, and (when the drawer can act) a button that opens the payment dialog. It carries no
// controls of its own: the same add-on used to appear here as a checkbox and again in a payment card
// as a line item, with neither saying which room it belonged to once it reached the money.
const AddOnBlock = styled.div`
  background: transparent;
  border: 0;
  border-top: 1px dashed ${theme.border};
  cursor: ${(props) => (props.$clickable ? "pointer" : "default")};
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-top: 0.35rem;
  padding-top: 0.4rem;
  text-align: left;
  width: 100%;
  &:hover {
    background: ${(props) => (props.$clickable ? "#faf9f6" : "transparent")};
  }
`;

const AddOnOpenHint = styled.span`
  color: #4a4f63;
  font-size: 0.74rem;
  font-weight: 700;
  margin-left: auto;
`;

const AddOnLine = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4rem;
  font-size: 0.92rem;
  color: ${theme.ink700};
`;

const AddOnLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  flex: 1 1 auto;
  font-weight: 800;
  color: ${theme.ink};
`;

// The booking's money in three numbers. "Held" is deliberately its own cell in green and is never
// folded into the paid total — the deposit was really paid AND is really refundable, and merging the
// two answers neither question.
const TotalStrip = styled.div`
  display: grid;
  gap: 0.4rem;
  grid-template-columns: repeat(3, 1fr);
  margin-bottom: 0.7rem;
`;

const TotalCell = styled.div`
  background: ${(props) => (props.$held ? "#e6f6ee" : "#faf9f6")};
  border: 1px solid ${(props) => (props.$held ? "#bfe3d2" : theme.border)};
  border-radius: 9px;
  padding: 0.45rem 0.55rem;
`;

const TotalLabel = styled.div`
  color: ${(props) => (props.$held ? "#1b6e56" : "#4a4f63")};
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.02em;
`;

const TotalValue = styled.div`
  color: ${(props) => (props.$held ? "#1b6e56" : theme.ink)};
  font-size: 0.92rem;
  font-weight: 800;
  margin-top: 0.1rem;
`;

// The single control that takes money on this booking. There is exactly one, and it opens the dialog.
const ChargeButton = styled.button`
  align-items: center;
  background: ${theme.ink};
  border: 0;
  border-radius: 9px;
  color: #ffffff;
  cursor: pointer;
  display: inline-flex;
  font-size: 0.88rem;
  font-weight: 800;
  gap: 0.35rem;
  justify-content: center;
  margin-bottom: 0.8rem;
  padding: 0.6rem 1rem;
  width: 100%;
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const EmptyPayments = styled.div`
  color: #4a4f63;
  font-size: 0.8rem;
  padding: 0.2rem 0 0.4rem;
`;

// Deliberately dark ink on the pale add-on background: text-muted grey washes out here, and this
// note carries the hour the door card must be set to.
const LateUntilNote = styled.span`
  align-items: center;
  color: #4a4f63;
  display: inline-flex;
  font-size: 0.78rem;
  font-weight: 700;
  gap: 0.3rem;
`;

const AddOnPrice = styled.span`
  font-weight: 700;
  color: ${theme.ink700};
`;

// At-a-glance "who processes this payment": Xendit (online) gets a branded indigo pill + bolt; anything
// else (cash/EDC/QRIS/transfer at the desk) is a neutral "Manual" pill.
const ProviderChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.12rem 0.5rem;
  border-radius: 6px;
  font-size: 0.72rem;
  font-weight: 800;
  background: ${(props) => (props.$xendit ? "#e8ecff" : "#eef1f5")};
  color: ${(props) => (props.$xendit ? "#3538cd" : theme.muted)};
  border: 1px solid ${(props) => (props.$xendit ? "#c7cffb" : theme.border)};
`;

// Destructive and rare: full width, its own section, never beside a routine control.
const CancelButton = styled.button`
  align-items: center;
  background: #ffffff;
  border: 1px solid #f7cfca;
  border-radius: 9px;
  color: #b42318;
  cursor: pointer;
  display: inline-flex;
  font-size: 0.86rem;
  font-weight: 800;
  gap: 0.35rem;
  justify-content: center;
  padding: 0.55rem 1rem;
  width: 100%;
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const ActionMsg = styled.div`
  padding: 0.55rem 0.75rem;
  border-radius: 10px;
  font-size: 0.82rem;
  font-weight: 600;
  color: ${(props) => (props.$ok ? "#166534" : "#991b1b")};
  background: ${(props) => (props.$ok ? "#e8f6ee" : "#fdecec")};
  border: 1px solid ${(props) => (props.$ok ? "#b7e0c6" : "#f2c4c0")};
`;

const NotesRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.3rem;
`;

// Change / Swap room buttons sit on the far right of the notes row, only for still-reserved lines.
const RoomMoveButtons = styled.div`
  margin-left: auto;
  display: inline-flex;
  gap: 0.3rem;
`;

const ChangeRoomButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.28rem 0.6rem;
  border: 1px solid ${theme.goldDark};
  border-radius: 8px;
  background: ${theme.goldSoft};
  color: ${theme.goldDark};
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: ${theme.goldDark};
    color: #ffffff;
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const OtaBox = styled.div`
  padding: 0.4rem 0.55rem;
  border: 1px solid #f0dfc0;
  border-radius: 8px;
  background: #fff8ec;
  font-size: 1rem;
  color: ${theme.ink700};
  white-space: pre-wrap;
  word-break: break-word;
`;

// Attempt history inline in the refund card. The counters answered "how many"; the destinations are
// what actually settle a "the link never arrived" conversation.
const AttemptStrip = styled.div`
  background: #f8f7f4;
  border: 1px solid ${theme.border};
  border-radius: 9px;
  margin-top: 0.5rem;
  padding: 0.45rem 0.6rem;
`;

const AttemptCount = styled.div`
  color: ${(props) => (props.$exhausted ? theme.danger : theme.ink700)};
  font-size: 0.78rem;
  font-weight: 800;
`;

const AttemptLine = styled.div`
  align-items: center;
  color: #4a4f63;
  display: flex;
  font-size: 0.75rem;
  gap: 0.35rem;
  margin-top: 0.25rem;
  word-break: break-all;
`;

const AttemptDot = styled.span`
  background: ${(props) => (props.$ok ? "#1b6e56" : theme.danger)};
  border-radius: 50%;
  flex: 0 0 auto;
  height: 7px;
  width: 7px;
`;

const AttemptEmpty = styled.div`
  color: #5e6278;
  font-size: 0.75rem;
  margin-top: 0.2rem;
`;

const InfoNote = styled.div`
  display: flex;
  gap: 0.4rem;
  align-items: flex-start;
  padding: 0.5rem 0.65rem;
  border-radius: 8px;
  background: ${theme.canvas};
  border: 1px solid ${theme.border};
  font-size: 1rem;
  color: ${theme.ink700};
`;

/* The payout failed and the guest is stuck on the verify page. Louder than InfoNote because the
 * symptom otherwise reaches the desk only as a phone call. */
const FailNote = styled.div`
  align-items: flex-start;
  background: ${theme.dangerBg};
  border: 1px solid ${theme.dangerBorder};
  border-radius: 8px;
  color: ${theme.danger};
  display: flex;
  font-size: 0.9rem;
  gap: 0.45rem;
  line-height: 1.5;
  padding: 0.55rem 0.7rem;
`;

const FailReason = styled.div`
  color: ${theme.ink700};
  font-size: 0.8rem;
  margin-top: 0.3rem;
  overflow-wrap: anywhere;
`;

const shimmer = keyframes`
  0% { opacity: 0.55; }
  50% { opacity: 1; }
  100% { opacity: 0.55; }
`;

const SkeletonBlock = styled.div`
  height: ${(props) => props.$h || "72px"};
  border-radius: 12px;
  background: #eceae4;
  animation: ${shimmer} 1.2s ease-in-out infinite;
`;

const ErrorBox = styled.div`
  padding: 1.5rem;
  text-align: center;
  color: ${theme.ink700};
`;

const RetryButton = styled.button`
  margin-top: 0.75rem;
  padding: 0.45rem 1rem;
  border: 1px solid ${theme.border};
  border-radius: 8px;
  background: #ffffff;
  font-weight: 700;
  cursor: pointer;
`;

const LINE_STATUS_META = {
  reserved: { key: "line_reserved", bg: "#fdf0e1", fg: "#b45309" },
  checked_in: { key: "line_checked_in", bg: "#e7f0fb", fg: "#1d5fa8" },
  checked_out: { key: "line_checked_out", bg: "#eef1f5", fg: "#334155" },
  canceled: { key: "line_canceled", bg: "#fdecec", fg: "#c0392b" },
};

// Per-room deposit-refund state → human label + colour, so the FO reads exactly where each room is in
// the check-out → HK-secure → refund progression (see RoomBookingLine#refund_overview_state).
const REFUND_STATE_META = {
  not_checked_out: { key: "rfs_not_checked_out", bg: "#eef1f5", fg: "#475569" },
  awaiting_hk: { key: "rfs_awaiting_hk", bg: "#fef3c7", fg: "#92400e" },
  review_cash: { key: "rfs_review_cash", bg: "#fee2e2", fg: "#991b1b" },
  held: { key: "rfs_held", bg: "#fef3c7", fg: "#92400e" },
  refundable: { key: "rfs_refundable", bg: "#dcfce7", fg: "#166534" },
  refundable_cash: { key: "rfs_refundable_cash", bg: "#dcfce7", fg: "#166534" },
  claimed: { key: "rfs_claimed", bg: "#dbeafe", fg: "#1e40af" },
  refunded: { key: "rfs_refunded", bg: "#dcfce7", fg: "#166534" },
  refunded_cash: { key: "rfs_refunded", bg: "#dcfce7", fg: "#166534" },
  kept: { key: "rfs_kept", bg: "#eef1f5", fg: "#475569" },
  not_eligible: { key: "rfs_not_eligible", bg: "#eef1f5", fg: "#475569" },
};

// Online (Xendit) refund payout status → colour. Neutral while pending, gold once the link is out,
// blue when the guest has claimed it, green on completion, red/grey for the dead states.
const ONLINE_REFUND_META = {
  pending: { bg: "#eef1f5", fg: "#475569" },
  payout_created: { bg: "#e0f2fe", fg: "#075985" },
  whatsapp_sent: { bg: "#f5eee0", fg: "#8c6d3f" },
  token_verified: { bg: "#dbeafe", fg: "#1e40af" },
  completed: { bg: "#dcfce7", fg: "#166534" },
  failed: { bg: "#fee2e2", fg: "#991b1b" },
  expired: { bg: "#f1f5f9", fg: "#64748b" },
  canceled: { bg: "#f1f5f9", fg: "#64748b" },
};

// Rendered as a <button> (`as="button"`): the whole row is the control, so there is exactly one
// place to click and no ambiguity about which payment an action belongs to.
const PayItem = styled.div`
  background: transparent;
  border: 0;
  border-bottom: 1px solid ${theme.border};
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.5rem 0;
  text-align: left;
  width: 100%;
  &:last-child {
    border-bottom: 0;
  }
  &:hover {
    background: #faf9f6;
  }
`;

const PayRowHint = styled.span`
  color: #4a4f63;
  font-size: 0.74rem;
  font-weight: 700;
  margin-left: auto;
`;

const PayItemTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: ${theme.ink700};
`;

const PayMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

const PayItemBottom = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.55rem;
  font-size: 0.86rem;
`;

const PayInvoice = styled.span`
  font-size: 0.78rem;
  color: ${theme.muted};
  font-variant-numeric: tabular-nums;
`;

// WhatsApp country codes for the send-voucher modal (mirrors the /admin dial-code picker).
const WaCard = styled.div`
  background: #ffffff;
  border-radius: 14px;
  padding: 1.5rem;
  width: min(420px, 92vw);
`;

const WaTitle = styled.h3`
  margin: 0 0 0.5rem;
  font-size: 1.1rem;
  font-weight: 800;
  color: ${theme.ink};
`;

const WaDesc = styled.p`
  margin: 0 0 1rem;
  font-size: 0.95rem;
  color: ${theme.ink700};
`;

const WaRow = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const WaSelect = styled.select`
  flex: 0 0 auto;
  max-width: 170px;
  padding: 0.6rem 0.5rem;
  border: 1px solid ${theme.border};
  border-radius: 8px;
  font-size: 1rem;
  background: #ffffff;
`;

const WaInput = styled.input`
  flex: 1 1 auto;
  min-width: 0;
  padding: 0.6rem 0.7rem;
  border: 1px solid ${theme.border};
  border-radius: 8px;
  font-size: 1rem;
`;

const WaHint = styled.div`
  margin-top: 0.4rem;
  font-size: 0.85rem;
  color: ${theme.muted};
`;

const WaErr = styled.div`
  margin-top: 0.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  color: #b91c1c;
`;

const WaActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.6rem;
  margin-top: 1.25rem;
`;

const WaCancel = styled.button`
  padding: 0.55rem 1rem;
  border: 1px solid ${theme.border};
  border-radius: 8px;
  background: ${theme.canvas};
  color: ${theme.ink};
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
`;

const WaSend = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.55rem 1.1rem;
  border: 0;
  border-radius: 8px;
  background: #1f9d55;
  color: #ffffff;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  &:hover {
    background: #178a49;
  }
`;

// In-app confirmation dialog (replaces window.confirm) for the heavier drawer actions — a title, a
// plain-language explanation, and an explicit Ya / Batal choice. Reuses the WaCard shell + backdrop.
const ConfirmBody = styled.p`
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.55;
  color: ${theme.ink700};
`;

const ConfirmNote = styled.p`
  margin: 0.85rem 0 0;
  padding: 0.7rem 0.85rem;
  border-radius: 8px;
  background: ${theme.goldSoft};
  border-left: 4px solid ${theme.goldDark};
  font-size: 0.9rem;
  line-height: 1.5;
  font-weight: 600;
  color: ${theme.ink};
`;

const ConfirmOk = styled(WaSend)`
  background: ${theme.goldDark};
  &:hover {
    background: #74592f;
  }
`;

// Inline group for a meta value plus its chips (voucher QR/WA, email edit + OTA warning).
const ChipRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
`;

const EditChip = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.22rem;
  border: 1.5px solid ${theme.border};
  border-radius: 7px;
  background: #ffffff;
  color: ${theme.ink700};
  cursor: pointer;
  line-height: 1;
  &:hover {
    background: ${theme.goldSoft};
    border-color: ${theme.goldDark};
    color: ${theme.goldDark};
  }
`;

const OtaWarn = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.12rem 0.45rem;
  border-radius: 6px;
  background: #fef3c7;
  color: #92400e;
  font-size: 0.72rem;
  font-weight: 800;
  cursor: help;
`;

// Reusable "needs attention" glow — pulsing amber. Applied to a value the FO must notice (e.g. an
// OTA-relay email that a paid Xendit refund code will never reach). Pair with <AttnMark> + a Tip.
const attnGlow = keyframes`
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(217, 119, 6, 0);
  }
  50% {
    box-shadow: 0 0 8px 1px rgba(217, 119, 6, 0.6);
  }
`;

const AttnValue = styled.span`
  padding: 0.05rem 0.3rem;
  border-radius: 6px;
  background: ${(props) => (props.$attn ? "#fffbeb" : "transparent")};
  animation: ${(props) => (props.$attn ? attnGlow : "none")} 1.6s ease-in-out
    infinite;
`;

const AttnMark = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  padding: 0.12rem 0.45rem;
  border-radius: 6px;
  background: #fde68a;
  color: #92400e;
  font-size: 0.72rem;
  font-weight: 800;
  cursor: help;
`;

const BookingDrawer = (props) => {
  const { t, lang } = useT();
  const lineId = props.lineId;
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [otaOpen, setOtaOpen] = useState(null);
  // Write-action state: one busy key at a time (double-submit guard), an inline result message, and
  // a dirty flag so closing after any mutation refreshes the page props once.
  const [busyKey, setBusyKey] = useState(null);
  const [voidRefundOpen, setVoidRefundOpen] = useState(false);
  const [payoutFetchOpen, setPayoutFetchOpen] = useState(false);
  const [payoutFetchError, setPayoutFetchError] = useState("");
  const [rescheduleLine, setRescheduleLine] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);
  // The payment dialog. It is the ONLY surface on this booking that takes money — the drawer keeps
  // the totals and the list of payments, and hands every charge decision to the dialog.
  const [payOpen, setPayOpen] = useState(() => Boolean(readPaymentId()));
  // Which payment the dialog has open, if any. Null = the composer.
  const [payId, setPayId] = useState(readPaymentId);
  // Mirror it into a ref so the document-level keydown (empty-deps closure below) can read the live
  // value: Escape closes the dialog first, and only then the drawer.
  const payOpenRef = useRef(false);
  const didMutateRef = useRef(false);

  const togglePay = (open) => {
    payOpenRef.current = open;
    setPayOpen(open);
    if (!open) setPayId(null);
  };

  const openPayment = (id) => {
    setPayId(id);
    payOpenRef.current = true;
    setPayOpen(true);
  };

  usePaymentUrlSync(payOpen ? payId : null);

  const handleClose = () => {
    if (didMutateRef.current) {
      router.reload({ preserveScroll: true });
    }
    props.onClose();
  };

  const act = async (key, path, body = {}, confirmText = null) => {
    if (busyKey) {
      return;
    }
    // Route the confirm through the in-app dialog (not window.confirm) for a consistent look.
    if (confirmText) {
      askConfirm({
        title: t("confirm_generic_title"),
        body: confirmText,
        confirmLabel: t("confirm_ok"),
        onConfirm: () => act(key, path, body),
      });
      return;
    }
    setBusyKey(key);
    setActionMsg(null);
    try {
      const res = await apiFetch(
        `/frontdesk_v2/bookings/${data.booking.id}/${path}`,
        {
          method: "POST",
          body: JSON.stringify({ ...body, line_id: lineId }),
        },
      );
      if (res?.data?.booking) {
        setData(res.data);
        didMutateRef.current = true;
        setActionMsg({ ok: true, text: res.message });
        // Some actions (send voucher) return a wa.me deep link for the FO to send from their WhatsApp.
        if (res.data.wa_url) {
          window.open(res.data.wa_url, "_blank", "noopener,noreferrer");
        }
      } else {
        setActionMsg({ ok: false, text: res?.message || t("error_body") });
      }
      setBusyKey(null);
      // Returned so a caller can react to a structured refusal — the late-checkout flow reads the
      // clashing arrivals out of res.data and re-posts with an acknowledgement.
      return res;
    } catch {
      setActionMsg({ ok: false, text: t("error_body") });
      setBusyKey(null);
      return null;
    }
  };

  /* Its own call rather than act(): act() decides "did this work?" by whether a payload came back,
   * so an error path can never refresh the drawer. Here the failed read is precisely the thing the
   * operator opened the dialog to see, and the endpoint returns the refreshed payload on both paths.
   */
  const fetchPayout = async () => {
    if (busyKey) return;
    setBusyKey("fetch_refund_payout");
    setPayoutFetchError("");
    const res = await apiFetch(
      `/frontdesk_v2/bookings/${data.booking.id}/fetch_refund_payout`,
      { method: "POST", body: JSON.stringify({ line_id: lineId }) },
    ).catch(() => null);
    setBusyKey(null);
    if (res?.data?.booking) {
      setData(res.data);
      didMutateRef.current = true;
    }
    setPayoutFetchError(res?.status === "BAD_REQUEST" ? res.message : "");
  };

  // Kill-switch: when OFF the drawer stays read-only (buttons hidden; endpoints refuse server-side).
  const canAct = Boolean(data?.toggles?.drawer_actions_enabled);

  // The drawer is rendered with key={lineId}, so a different line remounts it fresh (skeleton state).
  // reloadTick re-runs the fetch (retry button now; action refetches in the next increment).
  const [reloadTick, setReloadTick] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const refetch = useCallback(() => setReloadTick((tick) => tick + 1), []);
  // Manual refresh (header button): pull fresh booking data — e.g. after the guest says they paid and
  // the Xendit webhook has already updated the status server-side. Shows a spinner until it returns.
  const refresh = useCallback(() => {
    setRefreshing(true);
    refetch();
  }, [refetch]);

  // Enlarged voucher QR (photograph → scan at SelfQ) + send-voucher over WhatsApp (also arms the
  // 10-minute OTP-skip window so the guest walks straight through HGS check-in).
  const [qrOpen, setQrOpen] = useState(false);
  const [waOpen, setWaOpen] = useState(false);
  const [waCountry, setWaCountry] = useState("62");
  const [waManualCode, setWaManualCode] = useState("");
  const [waLocal, setWaLocal] = useState("");
  const [waError, setWaError] = useState("");

  // In-app confirmation dialog: { title, body, note, confirmLabel, onConfirm } or null.
  const [confirmState, setConfirmState] = useState(null);
  const [refundDialog, setRefundDialog] = useState(null);
  const askConfirm = (dialog) => setConfirmState(dialog);
  const runConfirm = () => {
    const dialog = confirmState;
    setConfirmState(null);
    dialog?.onConfirm?.();
  };

  // The RD- code + one-tap claim link always go to the guest's OWN registered contact (FO never sees
  // the code); language follows the FO's current UI language for the message template.
  const sendRefund = (channel) =>
    setRefundDialog({
      mode: "send",
      channel,
      onConfirm: () => {
        setRefundDialog(null);
        act("send_refund_token", "send_refund_token", {
          channel,
          language: lang,
        });
      },
    });

  const regenerateRefund = () =>
    askConfirm({
      title: t("confirm_regen_refund_title"),
      body: t("confirm_regen_refund_body"),
      note: t("confirm_regen_refund_note"),
      confirmLabel: t("confirm_regen_refund_ok"),
      onConfirm: () => act("regenerate_refund", "regenerate_refund", {}),
    });

  const generateRefund = () =>
    setRefundDialog({
      mode: "generate",
      onConfirm: () => {
        setRefundDialog(null);
        act("generate_refund", "generate_refund", {});
      },
    });

  // Change / Swap room for a still-reserved line (before check-in). Reuses the /admin flow via
  // RoomMoveModal; on success the modal returns the refreshed booking payload for the drawer.
  const [roomMove, setRoomMove] = useState(null); // { mode: "change"|"swap", line } | null

  const applyRoomMove = (refreshed, message) => {
    setData(refreshed);
    didMutateRef.current = true;
    setActionMsg({ ok: true, text: message });
  };

  // Inline guest-email edit (e.g. replace an OTA-relay address the guest never reads).
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailValue, setEmailValue] = useState("");
  const [emailError, setEmailError] = useState("");

  const openEmailModal = () => {
    setEmailValue(data?.guest?.email || "");
    setEmailError("");
    setEmailOpen(true);
  };

  const submitEmail = () => {
    const email = emailValue.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return setEmailError(t("email_invalid"));
    }
    setEmailError("");
    setEmailOpen(false);
    return act("update_email", "update_email", { email });
  };

  // Inline guest phone / WhatsApp edit (e.g. correct a wrong number so the check-in / refund link lands).
  const [phoneOpen, setPhoneOpen] = useState(false);
  const [phoneValue, setPhoneValue] = useState("");
  const [phoneError, setPhoneError] = useState("");

  const openPhoneModal = () => {
    setPhoneValue(data?.guest?.phone || "");
    setPhoneError("");
    setPhoneOpen(true);
  };

  const submitPhone = () => {
    const phone = phoneValue.replace(/\D/g, "");
    if (phone.length < 8) {
      return setPhoneError(t("phone_invalid"));
    }
    setPhoneError("");
    setPhoneOpen(false);
    return act("update_phone", "update_phone", { phone });
  };

  const openWaModal = () => {
    const detected = detectWaCountry(data?.guest?.phone || "");
    const known = WA_COUNTRIES.some(
      (country) => country.code === detected.code,
    );
    setWaCountry(known ? detected.code : "other");
    setWaManualCode(known ? "" : detected.code);
    setWaLocal(detected.local);
    setWaError("");
    setWaOpen(true);
  };

  const submitWaVoucher = () => {
    const code = (waCountry === "other" ? waManualCode : waCountry).replace(
      /\D/g,
      "",
    );
    const local = waLocal.replace(/\D/g, "").replace(/^0+/, "");
    if (!code) return setWaError(t("wa_err_code"));
    if (!local) return setWaError(t("wa_err_number"));
    setWaError("");
    setWaOpen(false);
    return act("send_wa_voucher", "send_whatsapp_voucher", {
      phone: code + local,
    });
  };

  useEffect(() => {
    let active = true;
    apiFetch(`/frontdesk_v2/lines/${lineId}/booking`)
      .then((res) => {
        if (!active) {
          return;
        }
        if (res?.data?.booking) {
          setData(res.data);
          setError(false);
        } else {
          setError(true);
        }
      })
      .catch(() => {
        if (active) {
          setError(true);
        }
      })
      .finally(() => {
        if (active) {
          setRefreshing(false);
        }
      });
    return () => {
      active = false;
    };
  }, [lineId, reloadTick]);

  // The page's 30s auto-refresh (usePoll -> router.reload) fires an Inertia "finish"; refetch the open
  // drawer on it too, so the drawer's data stays as fresh as the lists behind it.
  useEffect(() => {
    const off = router.on("finish", () => refetch());
    return off;
  }, [refetch]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape") {
        if (payOpenRef.current) {
          togglePay(false);
          return;
        }
        handleClose();
      }
    };
    document.addEventListener("keydown", onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, []);

  const currentPath = () => window.location.pathname;

  // Check-in/out normally happen at the SelfQ machine; these are the manual fallback, so each asks
  // for confirmation (in-app dialog) before firing to avoid a mistaken click moving the guest's status.
  const doCheckIn = (targetLineId) => {
    router.post(
      `/frontdesk_v2/lines/${targetLineId}/check_in`,
      { date: props.date, return_to: currentPath() },
      { preserveScroll: true },
    );
  };

  const doCheckOut = (targetLineId) => {
    router.post(
      `/frontdesk_v2/lines/${targetLineId}/check_out`,
      { date: props.date, return_to: currentPath() },
      { preserveScroll: true },
    );
  };

  const voidAndRegenerateRefund = () => {
    setVoidRefundOpen(false);
    act("void_and_regenerate_refund", "void_and_regenerate_refund", {});
  };

  const reopenTransitSlot = (line) =>
    askConfirm({
      title: t("transit_reopen_title"),
      body: t("transit_reopen_body")
        .replace("%{from}", line.transit_unused_from)
        .replace("%{to}", line.transit_unused_until),
      confirmLabel: t("transit_reopen_ok"),
      onConfirm: () =>
        router.post(
          `/frontdesk_v2/lines/${line.id}/reopen_transit_slot`,
          { date: props.date, return_to: currentPath() },
          { preserveScroll: true },
        ),
    });

  const checkIn = (targetLineId) =>
    askConfirm({
      title: t("confirm_checkin_title"),
      body: t("confirm_checkin_body"),
      confirmLabel: t("confirm_checkin_ok"),
      onConfirm: () => doCheckIn(targetLineId),
    });

  const checkOut = (targetLineId) =>
    askConfirm({
      title: t("confirm_checkout_title"),
      body: t("confirm_checkout_body"),
      confirmLabel: t("confirm_checkout_ok"),
      onConfirm: () => doCheckOut(targetLineId),
    });

  // --- Per-line add-ons (extra bed + early check-in) ------------------------------------------------

  // The booking's state as a sentence, at the top of the payment card. Same source the cart footer
  // uses, so the two surfaces can never tell the FO different stories.
  const chargeStateLabel = () => {
    const kind = data?.payment?.charge_intent?.kind;
    if (kind === "booking") return t("cart_intent_booking");
    if (kind === "deposit") return t("cart_intent_deposit");
    if (kind === "add_on") return t("pay_state_settled");
    return t("cart_intent_none");
  };

  const renderPrimary = () => {
    const action = data.primary_action || {};
    switch (action.key) {
      case "collect_payment":
        return (
          <PrimaryCard $bg="#fdf0e1" $border="#f3d3a3">
            <div>
              <PrimaryText>{t("pa_collect_payment")}</PrimaryText>
              <PrimaryHint>{t("pa_collect_payment_hint")}</PrimaryHint>
            </div>
            <PrimaryButton
              type="button"
              onClick={() =>
                document
                  .getElementById("drawer-payment")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
            >
              <Wallet size={15} />
              {t("sec_payment")}
            </PrimaryButton>
          </PrimaryCard>
        );
      case "open_admin_payment":
        return (
          <PrimaryCard $bg="#f3f7fc" $border="#c9dcf2">
            <PrimaryText>{t("pa_open_admin_payment")}</PrimaryText>
            <PrimaryButton
              as="a"
              href={data.booking.admin_url}
              target="_blank"
              rel="noopener"
            >
              <ExternalLink size={15} />
              {t("open_in_admin")}
            </PrimaryButton>
          </PrimaryCard>
        );
      case "check_in":
      case "check_out":
        // Check-in / check-out are done per room in the Kamar section below (no booking-level card).
        return null;
      case "refund_status":
        return (
          <PrimaryCard>
            <PrimaryText>{t("pa_refund_status")}</PrimaryText>
          </PrimaryCard>
        );
      default:
        return (
          <PrimaryCard $bg="#fdecec" $border="#f2c4c0">
            <PrimaryText>{t("pa_canceled")}</PrimaryText>
          </PrimaryCard>
        );
    }
  };

  // Deposit is settled but the guest has not checked in yet: nudge the FO through the QRH next steps
  // (send them to the selfq machine). Cash deposits also get the "hand over the receipt" reminder.
  const renderNextStep = () => {
    const line = data.lines.find(
      (row) => row.status === "reserved" && row.deposit?.status === "paid",
    );
    if (!line) {
      return null;
    }
    const isCash = line.deposit?.method !== "xendit";
    return (
      <NextStepCard>
        <NextStepTitle>
          <CheckCircle2 size={16} />
          {t("ns_title")}
        </NextStepTitle>
        <NextStepIntro>
          {isCash ? t("ns_intro_cash") : t("ns_intro_online")}
        </NextStepIntro>
        <NextStepList>
          <NextStepItem>{t("ns_step_1")}</NextStepItem>
          <NextStepItem>{t("ns_step_2")}</NextStepItem>
          <NextStepItem>{t("ns_step_3")}</NextStepItem>
        </NextStepList>
        {isCash ? <NextStepNote>{t("ns_cash_note")}</NextStepNote> : null}
      </NextStepCard>
    );
  };

  const renderLines = () => (
    <Section>
      <SectionTitle>{t("sec_rooms")}</SectionTitle>
      {data.lines.map((line) => {
        const statusMeta =
          LINE_STATUS_META[line.status] || LINE_STATUS_META.reserved;
        const comment = cleanComment(line.comment);
        return (
          <LineCard
            key={line.id}
            $anchor={line.is_anchor}
            $accent={statusMeta.fg}
          >
            <LineHead>
              <LineRoom>{line.room_code || "—"}</LineRoom>
              {line.room_status ? (
                <StatusBadge
                  meta={physicalStatusMeta(line.room_status)}
                  variant="soft"
                  size={13}
                />
              ) : null}
              <LineType>{line.room_type_name}</LineType>
              <StatusChip $bg={statusMeta.bg} $fg={statusMeta.fg}>
                {t(statusMeta.key)}
              </StatusChip>
              {/* Spelled out beside the status, because every money surface further down this
               * drawer reads as zero for a house-use stay and the reason has to be visible. */}
              {line.house_use ? (
                <StatusChip
                  $bg="#eaf1fb"
                  $fg="#2f4a70"
                  title={t("house_use_hint")}
                >
                  {t("house_use_badge")}
                </StatusChip>
              ) : null}
              {line.status === "reserved" ? (
                <SmallButton
                  type="button"
                  disabled={!line.can_check_in}
                  title={
                    line.can_check_in
                      ? ""
                      : (line.check_in_blockers || [])
                          .map((blocker) => t(`blk_${blocker}`))
                          .join(" · ")
                  }
                  onClick={() => checkIn(line.id)}
                >
                  <LogIn size={13} />
                  {t("manual_checkin_action")}
                </SmallButton>
              ) : null}
              {line.status === "checked_in" ? (
                <SmallButton
                  type="button"
                  disabled={!line.can_check_out}
                  onClick={() => checkOut(line.id)}
                >
                  <LogOut size={13} />
                  {t("manual_checkout_action")}
                </SmallButton>
              ) : null}
              {/* A transit guest who left early still has hours reserved. Offer them back so the room
                  can be sold again — deliberately a decision, not a checkout side effect, because the
                  guest's card stays valid for the window they bought. */}
              {line.transit_reopenable ? (
                <SmallButton
                  type="button"
                  onClick={() => reopenTransitSlot(line)}
                >
                  <RotateCcw size={13} />
                  {t("transit_reopen_action")}
                </SmallButton>
              ) : null}
            </LineHead>
            <LineDates>
              {line.stay_kind === "transit" ? (
                <>
                  {formatDate(line.check_in_date, lang, false)} ·{" "}
                  {timeWithDayPart(line.transit_from, t)} →{" "}
                  {timeWithDayPart(line.transit_to, t)}
                  {line.transit_duration_hours
                    ? ` · ${line.transit_duration_hours} ${t("transit_hours")}`
                    : ""}
                </>
              ) : (
                <>
                  {formatDate(line.check_in_date, lang, false)} →{" "}
                  {formatDate(line.check_out_date, lang, false)}
                  {line.nights ? ` · ${line.nights} ${t("nights")}` : ""}
                </>
              )}
              {line.wifi_usernames?.length
                ? ` · ${t("wifi_label")}: ${line.wifi_usernames.join(", ")}`
                : ""}
            </LineDates>
            {line.deposit ? (
              <LineDeposit>
                <span>
                  {t("bd_deposit")}: {line.deposit.amount_formatted}
                </span>
                <PayBadge status={line.deposit.status} />
              </LineDeposit>
            ) : null}
            {line.status === "reserved" &&
            !line.can_check_in &&
            line.check_in_blockers?.length ? (
              <Blockers>
                {t("checkin_blocked_title")}{" "}
                {line.check_in_blockers
                  .map((blocker) => t(`blk_${blocker}`))
                  .join(" · ")}
              </Blockers>
            ) : null}
            <NotesRow>
              <FoNotesButton
                counts={data.notes_counts?.[line.id]}
                onClick={() =>
                  props.onOpenComments({
                    id: line.id,
                    room_code: line.room_code,
                    guest_name: data.guest.full_name,
                  })
                }
              />
              {comment ? (
                <GuestNotesButton
                  hasNote
                  active={otaOpen === line.id}
                  onClick={() =>
                    setOtaOpen(otaOpen === line.id ? null : line.id)
                  }
                />
              ) : null}
              {canAct ? (
                <RoomMoveButtons>
                  {/* Server-computed, all of them: a button the endpoint would refuse teaches the
                      rule at the worst possible moment. One button now — move and swap were two
                      halves of one decision, and the server resolves which applies. */}
                  {line.can_move_room ? (
                    <ChangeRoomButton
                      type="button"
                      disabled={busyKey !== null}
                      onClick={() => setRoomMove({ line })}
                    >
                      <ArrowLeftRight size={13} />
                      {t("act_move_room")}
                    </ChangeRoomButton>
                  ) : null}
                  {line.can_reschedule ? (
                    <ChangeRoomButton
                      type="button"
                      disabled={busyKey !== null}
                      onClick={() => setRescheduleLine(line)}
                    >
                      <CalendarRange size={13} />
                      {t("act_change_dates")}
                    </ChangeRoomButton>
                  ) : null}
                </RoomMoveButtons>
              ) : null}
            </NotesRow>
            {otaOpen === line.id ? <OtaBox>{comment}</OtaBox> : null}
            {renderLineAddOns(line)}
          </LineCard>
        );
      })}
    </Section>
  );

  // What has been sold against THIS room, read-only.
  //
  // Every control that used to live here — include toggles, remove buttons, the add button — moved
  // into the payment dialog. They were the redundancy the front desk kept tripping over: the same
  // add-on appeared here as a checkbox and again in a payment card as a line item, and neither said
  // which room it belonged to once it reached the money.
  //
  // What stays is the one fact that exists nowhere else: the hour the door card must be re-encoded to.
  const renderLineAddOns = (line) => {
    if (line.status === "canceled") {
      return null;
    }
    const early = line.early_checkin || {};
    const late = line.late_checkout || {};
    const beds = line.extra_beds || [];
    const purchases = late.purchases || [];
    const hasAny =
      beds.length > 0 ||
      purchases.length > 0 ||
      Boolean(early.exists) ||
      Boolean(late.paid_until);
    if (!hasAny) {
      return null;
    }

    return (
      <AddOnBlock
        {...(canAct
          ? { as: "button", type: "button", onClick: () => togglePay(true) }
          : {})}
        $clickable={canAct}
      >
        <AddOnLine>
          <AddOnLabel>
            <BedDouble size={13} />
            {t("addon_title")}
          </AddOnLabel>
          {canAct ? (
            <AddOnOpenHint>{t("pay_open_detail")}</AddOnOpenHint>
          ) : null}
        </AddOnLine>
        {beds.map((bed) => (
          <AddOnLine key={bed.id}>
            <AddOnLabel>
              {t("addon_extra_bed")} ×{bed.quantity}
            </AddOnLabel>
            <AddOnPrice>{bed.total_formatted}</AddOnPrice>
            <PayBadge status="pending" />
          </AddOnLine>
        ))}
        {early.exists ? (
          <AddOnLine>
            <AddOnLabel>
              <Clock size={13} />
              {t("addon_early")}
            </AddOnLabel>
            <PayBadge status={early.paid ? "paid" : "pending"} />
          </AddOnLine>
        ) : null}
        {purchases.map((row) => (
          <AddOnLine key={row.id}>
            <AddOnLabel>
              <Clock size={13} />
              {t("addon_late_checkout")} {row.label}
            </AddOnLabel>
            <AddOnPrice>{row.total_formatted}</AddOnPrice>
            <PayBadge status={row.status === "paid" ? "paid" : "pending"} />
          </AddOnLine>
        ))}
        {late.paid_until ? (
          // The hour the card must be re-encoded to. The main app never pushes this to the kiosk —
          // the lock is programmed in the vendor's own software — so this line is the only place the
          // paid hours and the door agree. Always shown, including when the drawer is read-only.
          <AddOnLine>
            <LateUntilNote>
              <Clock size={12} />
              {t("late_paid_until")} <strong>{late.paid_until}</strong>
            </LateUntilNote>
          </AddOnLine>
        ) : null}
      </AddOnBlock>
    );
  };

  // One payment row (room-rate, deposit-collection, or add-on-only), shared by the payment + add-on
  // sections. The header label is chosen from the row's kind.
  // The WHOLE row opens the payment; there are no inline buttons. Every control that used to sit here
  // now lives on the payment's own screen, where the payment being acted on is the one in front of
  // the FO rather than "the newest one of this kind".
  const payRow = (row) => {
    const label = row.add_on_only
      ? t("sec_add_on")
      : row.deposit_collection
        ? t("sec_deposit")
        : t("sec_payment");
    return (
      <PayItem
        key={row.id}
        as="button"
        type="button"
        onClick={() => openPayment(row.id)}
      >
        <PayItemTop>
          <span>{label}</span>
          <PayMeta>
            <ProviderChip $xendit={row.provider === "xendit"}>
              {row.provider === "xendit" ? (
                <Zap size={11} />
              ) : (
                <Banknote size={11} />
              )}
              {row.provider === "xendit"
                ? "Xendit"
                : row.payment_method || t("provider_manual")}
            </ProviderChip>
            <PayBadge status={row.status} />
          </PayMeta>
        </PayItemTop>
        <PayItemBottom>
          <strong>{row.amount_formatted}</strong>
          {row.invoice_no ? (
            <PayInvoice>
              {t("invoice_no")}: {row.invoice_no}
            </PayInvoice>
          ) : null}
          <PayRowHint>{t("pay_open_detail")}</PayRowHint>
        </PayItemBottom>
      </PayItem>
    );
  };

  // The booking's money in three numbers plus the list of payments already raised. Every decision
  // about WHAT to charge now happens in the dialog, so this surface has exactly one button.
  //
  // It replaces three sibling cards — Pembayaran, Deposit and Pembayaran Add-on — of which two were
  // inert on any given booking, each with its own voucher field, its own cash/link buttons and its
  // own include-toggles. Which one was live depended on a state the FO had to infer by scrolling.
  const renderTagihan = () => {
    const totals = data.payment?.totals || {};
    const payments = data.payment?.payments || [];
    return (
      <>
        <TotalStrip>
          <TotalCell>
            <TotalLabel>{t("pay_unbilled")}</TotalLabel>
            <TotalValue>{totals.unbilled_formatted}</TotalValue>
          </TotalCell>
          <TotalCell $held>
            <TotalLabel $held>{t("pay_held_total")}</TotalLabel>
            <TotalValue $held>{totals.held_formatted}</TotalValue>
          </TotalCell>
          <TotalCell>
            <TotalLabel>{t("pay_paid_total")}</TotalLabel>
            <TotalValue>{totals.paid_formatted}</TotalValue>
          </TotalCell>
        </TotalStrip>
        {canAct ? (
          <ChargeButton
            type="button"
            disabled={busyKey !== null}
            onClick={() => togglePay(true)}
          >
            <Receipt size={14} />
            {data.payment?.folio?.any ? t("pay_open") : t("pay_open_empty")}
          </ChargeButton>
        ) : null}
        {payments.length ? (
          payments.map((row) => payRow(row))
        ) : (
          <EmptyPayments>{t("pay_no_payments")}</EmptyPayments>
        )}
      </>
    );
  };

  const renderRefund = () => {
    const refund = data.refund;
    const hasAny = refund.states.some((state) => state.state !== "none");
    return (
      <Section>
        <SectionTitle>{t("sec_refund")}</SectionTitle>
        {hasAny ? (
          <>
            <KV>
              <span>{t("rf_refundable")}</span>
              <strong>{refund.refundable_now_formatted}</strong>
            </KV>
            <KV>
              <span>{t("rf_review")}</span>
              <strong>{refund.pending_review_formatted}</strong>
            </KV>
            {refund.latest_batch ? (
              <KV>
                <span>{t("rf_batch")}</span>
                <strong>{refund.latest_batch.status}</strong>
              </KV>
            ) : null}
            {refund.states
              .filter((state) => state.state !== "none")
              .map((state) => {
                const meta = REFUND_STATE_META[state.state];
                return (
                  <Fragment key={state.line_id}>
                    <KV>
                      <span>{state.room_code || "—"}</span>
                      {meta ? (
                        <StatusChip $bg={meta.bg} $fg={meta.fg}>
                          {t(meta.key)}
                        </StatusChip>
                      ) : (
                        <strong>
                          {String(state.state).replace(/_/g, " ")}
                        </strong>
                      )}
                    </KV>
                    {canAct && state.state === "refundable_cash" ? (
                      <ActionBar>
                        <SmallButton
                          type="button"
                          disabled={busyKey !== null}
                          onClick={() =>
                            askConfirm({
                              title: t("confirm_record_cash_refund_title"),
                              body: t("confirm_record_cash_refund_body"),
                              note: t("confirm_record_cash_refund_note"),
                              confirmLabel: t("confirm_record_cash_refund_ok"),
                              onConfirm: () =>
                                act(
                                  "record_cash_refund",
                                  "record_cash_refund",
                                  {
                                    refund_line_id: state.line_id,
                                    cash_refund_confirmed: true,
                                  },
                                ),
                            })
                          }
                        >
                          <Banknote size={13} />
                          {t("act_record_cash_refund")}
                          {state.cash_refund_amount
                            ? ` · ${state.cash_refund_amount}`
                            : ""}
                        </SmallButton>
                      </ActionBar>
                    ) : null}
                  </Fragment>
                );
              })}
            {refund.online_refund_outstanding ? (
              <InfoNote>{t("dep_online_note")}</InfoNote>
            ) : null}
            {canAct && !refund.online && refund.can_generate ? (
              <>
                <ActionBar>
                  <SmallButton
                    type="button"
                    disabled={busyKey !== null}
                    onClick={generateRefund}
                  >
                    <RefreshCw size={13} />
                    {t("act_generate_refund")}
                  </SmallButton>
                </ActionBar>
                <InfoNote>{t("rf_generate_hint")}</InfoNote>
              </>
            ) : null}
            {refund.online ? (
              <>
                <KV>
                  <span>{t("rf_online_status")}</span>
                  <StatusChip
                    $bg={
                      ONLINE_REFUND_META[refund.online.status]?.bg || "#eef1f5"
                    }
                    $fg={
                      ONLINE_REFUND_META[refund.online.status]?.fg ||
                      theme.ink700
                    }
                  >
                    {t(`rfo_${refund.online.status}`)}
                  </StatusChip>
                </KV>
                {refund.online.expires_at ? (
                  <InfoNote>
                    {t("rf_expires")}:{" "}
                    {formatDateTime(refund.online.expires_at, lang)}
                  </InfoNote>
                ) : null}
                {/* Always visible, not only inside the confirmation: how many sends have been made,
                    where the last one went and whether it landed. Without this the only way to learn
                    anything was to press send again. */}
                <AttemptStrip>
                  <AttemptCount
                    $exhausted={
                      Number(refund.online.fo_attempts_max || 0) > 0 &&
                      Number(refund.online.fo_attempts_used || 0) >=
                        Number(refund.online.fo_attempts_max || 0)
                    }
                  >
                    {t("rf_attempts_used")
                      .replace("%{used}", refund.online.fo_attempts_used || 0)
                      .replace("%{max}", refund.online.fo_attempts_max || 0)}
                  </AttemptCount>
                  {(refund.online.attempts || []).length === 0 ? (
                    <AttemptEmpty>{t("rf_attempts_none")}</AttemptEmpty>
                  ) : (
                    (refund.online.attempts || [])
                      .slice(0, 3)
                      .map((attempt) => (
                        <AttemptLine key={attempt.id}>
                          <AttemptDot $ok={attempt.delivered} />
                          <span>
                            {attempt.channel === "email" ? "Email" : "WA"} ·{" "}
                            {attempt.destination || "—"} ·{" "}
                            {attempt.delivered
                              ? t("refund_attempt_sent")
                              : t("refund_attempt_failed")}
                          </span>
                        </AttemptLine>
                      ))
                  )}
                </AttemptStrip>
                {canAct ? (
                  <>
                    {refund.online.code_sendable ? (
                      <ActionBar>
                        <SmallButton
                          type="button"
                          disabled={busyKey !== null}
                          onClick={() => sendRefund("whatsapp")}
                        >
                          <MessageCircle size={13} />
                          {t("act_send_wa")}
                        </SmallButton>
                        <SmallButton
                          type="button"
                          disabled={busyKey !== null}
                          onClick={() => sendRefund("email")}
                        >
                          <Mail size={13} />
                          {t("act_send_email")}
                        </SmallButton>
                      </ActionBar>
                    ) : null}
                    {refund.online.can_sync ||
                    refund.online.can_regenerate ||
                    refund.void_regenerate?.available ||
                    refund.void_regenerate?.cooling_down ? (
                      <ActionBar>
                        {refund.online.can_sync ? (
                          <SmallButton
                            type="button"
                            disabled={busyKey !== null}
                            onClick={() =>
                              act("sync_refund", "sync_refund", {})
                            }
                          >
                            <RotateCw size={13} />
                            {t("act_sync_refund")}
                          </SmallButton>
                        ) : null}
                        {refund.online.can_regenerate ? (
                          <SmallButton
                            type="button"
                            disabled={busyKey !== null}
                            onClick={regenerateRefund}
                          >
                            <RefreshCw size={13} />
                            {t("act_regenerate_refund")}
                          </SmallButton>
                        ) : null}
                        {/* Only when the current link is genuinely LIVE. Offering it on a dead payout
                            would invite the front desk to "fix" things that are not broken. */}
                        {refund.void_regenerate?.available ? (
                          <SmallButton
                            type="button"
                            disabled={busyKey !== null}
                            onClick={() => setVoidRefundOpen(true)}
                          >
                            <AlertTriangle size={13} />
                            {t("void_refund_action")}
                          </SmallButton>
                        ) : null}
                        {refund.void_regenerate?.cooling_down ? (
                          <InfoNote>{t("void_refund_cooling")}</InfoNote>
                        ) : null}
                      </ActionBar>
                    ) : null}
                    {refund.online.payout_failure ? (
                      <FailNote>
                        <AlertTriangle
                          size={15}
                          style={{ flexShrink: 0, marginTop: 2 }}
                        />
                        <div>
                          <strong>
                            {refund.online.payout_failure.needs_email_fix
                              ? t("payout_fail_email")
                              : t("payout_fail_generic")}
                          </strong>
                          <FailReason>
                            {t("payout_fail_attempts").replace(
                              "%{n}",
                              refund.online.payout_failure.attempts,
                            )}
                            {refund.online.payout_failure.reason
                              ? ` · ${refund.online.payout_failure.reason}`
                              : ` · ${t("payout_fail_no_reason")}`}
                            {refund.online.payout_failure.error_code
                              ? ` (${refund.online.payout_failure.error_code})`
                              : ""}
                          </FailReason>
                        </div>
                      </FailNote>
                    ) : null}
                    <InfoNote>{t("rf_send_hint")}</InfoNote>
                  </>
                ) : null}
              </>
            ) : null}
            {/* Outside the `online` block on purpose: that block is nil once a payout completes,
                and a completed payout is the one most worth reading back. Admin-only, and the
                endpoint re-gates — this drawer's own gate admits staff_fo. */}
            {refund.payout_fetch?.available ? (
              <ActionBar>
                <SmallButton
                  type="button"
                  onClick={() => setPayoutFetchOpen(true)}
                >
                  <FileSearch size={13} />
                  {t("payout_fetch_action")}
                </SmallButton>
              </ActionBar>
            ) : null}
          </>
        ) : (
          <KV>
            <span>{t("rf_none")}</span>
          </KV>
        )}
      </Section>
    );
  };

  return (
    <>
      <Overlay onClick={handleClose} />
      <Panel role="dialog" aria-modal="true" aria-label={t("drawer_title")}>
        <Head>
          <div>
            <HeadGuest>
              {data ? (
                <BookingSourceIcon
                  sourceKey={data.booking.source_key}
                  label={
                    data.booking.source_key === "walkin"
                      ? t("source_walkin")
                      : t("source_unknown")
                  }
                />
              ) : null}
              {data ? data.guest.full_name || "—" : t("loading")}
            </HeadGuest>
            {data ? (
              <HeadMeta>
                <MetaLabel>{t("voucher")}</MetaLabel>
                <MetaValue>
                  <ChipRow>
                    <span>{data.booking.voucher_code || "—"}</span>
                    {data.booking.voucher_code && data.booking.voucher_qr ? (
                      <TipWrap>
                        <ChipButton
                          type="button"
                          onClick={() => setQrOpen(true)}
                          aria-label={t("show_qr")}
                        >
                          <QrCode size={14} /> QR
                        </ChipButton>
                        <Tip className="fdtip">{t("qr_tip")}</Tip>
                      </TipWrap>
                    ) : null}
                    {data.booking.voucher_code && canAct ? (
                      <TipWrap>
                        <ChipButton
                          type="button"
                          onClick={openWaModal}
                          disabled={busyKey === "send_wa_voucher"}
                          aria-label={t("wa_voucher")}
                        >
                          <MessageCircle size={14} /> WA
                        </ChipButton>
                        <Tip className="fdtip">{t("wa_tip")}</Tip>
                      </TipWrap>
                    ) : null}
                  </ChipRow>
                </MetaValue>

                <MetaLabel>{t("source")}</MetaLabel>
                <MetaValue>{data.booking.source || "—"}</MetaValue>

                <MetaLabel>{t("phone")}</MetaLabel>
                <MetaValue>
                  <ChipRow>
                    <span>{data.guest.phone || "—"}</span>
                    {canAct ? (
                      <TipWrap>
                        <EditChip
                          type="button"
                          onClick={openPhoneModal}
                          disabled={busyKey === "update_phone"}
                          aria-label={t("phone_edit")}
                        >
                          <Pencil size={13} />
                        </EditChip>
                        <Tip className="fdtip">{t("phone_edit_tip")}</Tip>
                      </TipWrap>
                    ) : null}
                  </ChipRow>
                </MetaValue>

                <MetaLabel>{t("email")}</MetaLabel>
                <MetaValue>
                  <ChipRow>
                    <AttnValue $attn={data.guest.email_attention}>
                      {data.guest.email || "—"}
                    </AttnValue>
                    {canAct ? (
                      <TipWrap>
                        <EditChip
                          type="button"
                          onClick={openEmailModal}
                          disabled={busyKey === "update_email"}
                          aria-label={t("email_edit")}
                        >
                          <Pencil size={13} />
                        </EditChip>
                        <Tip className="fdtip">{t("email_edit_tip")}</Tip>
                      </TipWrap>
                    ) : null}
                    {data.guest.email_attention ? (
                      <TipWrap>
                        <AttnMark>
                          <AlertTriangle size={12} />{" "}
                          {t("email_attention_badge")}
                        </AttnMark>
                        <Tip className="fdtip">{t("email_attention_tip")}</Tip>
                      </TipWrap>
                    ) : data.guest.email_is_ota ? (
                      <TipWrap>
                        <OtaWarn>
                          <AlertTriangle size={12} /> {t("email_ota_badge")}
                        </OtaWarn>
                        <Tip className="fdtip">{t("email_ota_tip")}</Tip>
                      </TipWrap>
                    ) : null}
                  </ChipRow>
                </MetaValue>
              </HeadMeta>
            ) : null}
          </div>
          <HeadActions>
            <RefreshButton
              type="button"
              data-spin={refreshing ? "true" : "false"}
              disabled={refreshing}
              aria-label={t("drawer_refresh")}
              title={t("drawer_refresh")}
              onClick={refresh}
            >
              <RotateCw size={15} />
            </RefreshButton>
            {data ? (
              <AdminLink
                href={data.booking.admin_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink size={13} />
                {t("open_in_admin")}
              </AdminLink>
            ) : null}
            <CloseButton
              type="button"
              aria-label={t("close")}
              onClick={handleClose}
            >
              <X size={16} />
            </CloseButton>
          </HeadActions>
        </Head>
        <Body>
          {error ? (
            <ErrorBox>
              {t("error_body")}
              <div>
                <RetryButton
                  type="button"
                  onClick={() => {
                    setError(false);
                    refetch();
                  }}
                >
                  {t("retry")}
                </RetryButton>
              </div>
            </ErrorBox>
          ) : data ? (
            <>
              {actionMsg ? (
                <ActionMsg $ok={actionMsg.ok}>{actionMsg.text}</ActionMsg>
              ) : null}
              {renderPrimary()}
              {renderNextStep()}
              {renderLines()}
              {canAct && data.booking?.can_cancel ? (
                <Section>
                  <SectionTitle>{t("act_cancel_booking")}</SectionTitle>
                  <CancelButton
                    type="button"
                    disabled={busyKey !== null}
                    onClick={() => setCancelOpen(true)}
                  >
                    <Ban size={14} />
                    {t("act_cancel_booking")}
                  </CancelButton>
                </Section>
              ) : null}
              <Section id="drawer-payment">
                <SectionTitle>{t("sec_payment")}</SectionTitle>
                {renderTagihan()}
              </Section>
              {renderRefund()}
            </>
          ) : (
            <>
              <SkeletonBlock $h="64px" />
              <SkeletonBlock $h="160px" />
              <SkeletonBlock $h="120px" />
              <SkeletonBlock $h="90px" />
            </>
          )}
        </Body>
      </Panel>
      {payOpen && data ? (
        <PaymentDialog
          data={data}
          lineId={lineId}
          paymentId={payId}
          onOpenPayment={setPayId}
          stateLabel={chargeStateLabel()}
          busy={busyKey !== null}
          onRefresh={(payload) => {
            setData(payload);
            didMutateRef.current = true;
          }}
          onConfirm={askConfirm}
          onClose={() => togglePay(false)}
        />
      ) : null}
      {rescheduleLine ? (
        <RescheduleModal
          bookingId={data.booking.id}
          line={rescheduleLine}
          onClose={() => setRescheduleLine(null)}
          onChanged={(payload, message) => {
            setRescheduleLine(null);
            setData(payload);
            didMutateRef.current = true;
            setActionMsg({ ok: true, text: message });
          }}
        />
      ) : null}
      {cancelOpen ? (
        <CancelBookingDialog
          bookingId={data.booking.id}
          lineId={lineId}
          onClose={() => setCancelOpen(false)}
          onCanceled={(payload, message) => {
            setCancelOpen(false);
            setData(payload);
            didMutateRef.current = true;
            setActionMsg({ ok: true, text: message });
          }}
        />
      ) : null}
      {refundDialog ? (
        <RefundSendDialog
          mode={refundDialog.mode}
          channel={refundDialog.channel}
          refund={data?.refund?.online}
          onConfirm={refundDialog.onConfirm}
          onCancel={() => setRefundDialog(null)}
        />
      ) : null}
      {payoutFetchOpen ? (
        <PayoutFetchDialog
          info={data?.refund?.payout_fetch}
          busy={busyKey !== null}
          error={payoutFetchError}
          onFetch={fetchPayout}
          onClose={() => {
            setPayoutFetchOpen(false);
            setPayoutFetchError("");
          }}
        />
      ) : null}
      {voidRefundOpen ? (
        <VoidRefundDialog
          info={data?.refund?.void_regenerate}
          guestName={data?.guest?.full_name}
          busy={busyKey !== null}
          onConfirm={voidAndRegenerateRefund}
          onCancel={() => setVoidRefundOpen(false)}
        />
      ) : null}
      {qrOpen && data?.booking?.voucher_qr ? (
        <QrBackdrop onClick={() => setQrOpen(false)}>
          <QrCard onClick={(event) => event.stopPropagation()}>
            <QrLargeImg src={data.booking.voucher_qr} alt={t("voucher")} />
            <QrCodeText>{data.booking.voucher_code}</QrCodeText>
            <QrHint>{t("qr_hint")}</QrHint>
            <QrCloseButton type="button" onClick={() => setQrOpen(false)}>
              {t("close")}
            </QrCloseButton>
          </QrCard>
        </QrBackdrop>
      ) : null}
      {waOpen && data?.booking ? (
        <QrBackdrop onClick={() => setWaOpen(false)}>
          <WaCard onClick={(event) => event.stopPropagation()}>
            <WaTitle>{t("wa_voucher")}</WaTitle>
            <WaDesc>
              {t("wa_modal_desc")} <strong>{data.booking.voucher_code}</strong>
            </WaDesc>
            <WaRow>
              <WaSelect
                value={waCountry}
                onChange={(event) => setWaCountry(event.target.value)}
              >
                {WA_COUNTRIES.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.flag} +{country.code} {country.name}
                  </option>
                ))}
                <option value="other">🌐 {t("wa_other")}</option>
              </WaSelect>
              <WaInput
                type="text"
                inputMode="numeric"
                autoFocus
                placeholder="81234567890"
                value={waLocal}
                onChange={(event) => setWaLocal(event.target.value)}
              />
            </WaRow>
            {waCountry === "other" ? (
              <WaInput
                style={{ marginTop: "0.5rem", width: "100%" }}
                type="text"
                inputMode="numeric"
                placeholder={t("wa_code_placeholder")}
                value={waManualCode}
                onChange={(event) => setWaManualCode(event.target.value)}
              />
            ) : null}
            <WaHint>{t("wa_number_hint")}</WaHint>
            {waError ? <WaErr>{waError}</WaErr> : null}
            <WaActions>
              <WaCancel type="button" onClick={() => setWaOpen(false)}>
                {t("close")}
              </WaCancel>
              <WaSend type="button" onClick={submitWaVoucher}>
                <MessageCircle size={15} />
                {t("wa_send")}
              </WaSend>
            </WaActions>
          </WaCard>
        </QrBackdrop>
      ) : null}
      {confirmState ? (
        <QrBackdrop onClick={() => setConfirmState(null)}>
          <WaCard onClick={(event) => event.stopPropagation()}>
            <WaTitle>{confirmState.title}</WaTitle>
            <ConfirmBody>{confirmState.body}</ConfirmBody>
            {confirmState.note ? (
              <ConfirmNote>{confirmState.note}</ConfirmNote>
            ) : null}
            <WaActions>
              <WaCancel type="button" onClick={() => setConfirmState(null)}>
                {t("confirm_cancel")}
              </WaCancel>
              <ConfirmOk type="button" onClick={runConfirm}>
                <Check size={15} />
                {confirmState.confirmLabel}
              </ConfirmOk>
            </WaActions>
          </WaCard>
        </QrBackdrop>
      ) : null}
      {roomMove ? (
        <RoomMoveModal
          bookingId={data.booking.id}
          changeLineId={roomMove.line.id}
          anchorLineId={lineId}
          currentCode={roomMove.line.room_code}
          onChanged={applyRoomMove}
          onClose={() => setRoomMove(null)}
        />
      ) : null}
      {emailOpen && data?.guest ? (
        <QrBackdrop onClick={() => setEmailOpen(false)}>
          <WaCard onClick={(event) => event.stopPropagation()}>
            <WaTitle>{t("email_edit")}</WaTitle>
            <WaDesc>{t("email_edit_desc")}</WaDesc>
            <WaInput
              type="email"
              inputMode="email"
              autoFocus
              style={{ width: "100%" }}
              placeholder="nama@email.com"
              value={emailValue}
              onChange={(event) => setEmailValue(event.target.value)}
            />
            {emailError ? <WaErr>{emailError}</WaErr> : null}
            <WaActions>
              <WaCancel type="button" onClick={() => setEmailOpen(false)}>
                {t("confirm_cancel")}
              </WaCancel>
              <WaSend type="button" onClick={submitEmail}>
                <Check size={15} />
                {t("email_save")}
              </WaSend>
            </WaActions>
          </WaCard>
        </QrBackdrop>
      ) : null}
      {phoneOpen && data?.guest ? (
        <QrBackdrop onClick={() => setPhoneOpen(false)}>
          <WaCard onClick={(event) => event.stopPropagation()}>
            <WaTitle>{t("phone_edit")}</WaTitle>
            <WaDesc>{t("phone_edit_desc")}</WaDesc>
            <PhoneField value={phoneValue} onChange={setPhoneValue} autoFocus />
            <WaHint>{t("wa_number_hint")}</WaHint>
            {phoneError ? <WaErr>{phoneError}</WaErr> : null}
            <WaActions>
              <WaCancel type="button" onClick={() => setPhoneOpen(false)}>
                {t("confirm_cancel")}
              </WaCancel>
              <WaSend type="button" onClick={submitPhone}>
                <Check size={15} />
                {t("phone_save")}
              </WaSend>
            </WaActions>
          </WaCard>
        </QrBackdrop>
      ) : null}
    </>
  );
};

export default BookingDrawer;
